import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { usePresentation } from "@/contexts/PresentationContext";
import {
  Star, Trash2, ArrowRight, Landmark, Pickaxe, FileText, ShieldAlert,
  Banknote, Gavel, Award, Plus, Search, ChevronRight,
} from "lucide-react";

/**
 * Executive Watchlist — presentation-only. Items are persisted in localStorage.
 * Live status is enriched by re-reading the source endpoints when the page loads.
 * Never modifies operational records or backend state.
 */

const TYPE_META = {
  parcel:       { icon: Landmark,   accent: "#C99A2E", label: "Parcel",       to: "/app/land" },
  mineral:      { icon: Pickaxe,    accent: "#C99A2E", label: "Mineral right",to: "/app/mining" },
  application:  { icon: FileText,   accent: "#6BB88A", label: "Application",  to: "/app/applications" },
  dispute:      { icon: Gavel,      accent: "#E67E7E", label: "Dispute",      to: "/app/disputes" },
  revenue:      { icon: Banknote,   accent: "#4FA3D1", label: "Revenue",      to: "/app/revenue" },
  risk:         { icon: ShieldAlert,accent: "#E67E7E", label: "Risk",         to: "/app/gsros" },
  kpi:          { icon: Award,      accent: "#C99A2E", label: "KPI",          to: "/app/executive" },
  province:     { icon: Landmark,   accent: "#7B2FBE", label: "Province",     to: "/app/gsros" },
};

const useSuggested = () => {
  const mkt = useQuery({ queryKey: ["watch-mkt"], queryFn: async () => (await api.get("/cadastre360/sovereign/marketplace")).data });
  const risk = useQuery({ queryKey: ["watch-risk"], queryFn: async () => (await api.get("/cadastre360/gsros/national-risk-register")).data });
  const perf = useQuery({ queryKey: ["watch-prov"], queryFn: async () => (await api.get("/cadastre360/gsros/provincial-dashboards")).data });

  const suggestions = [];
  (mkt.data?.mining_opportunities || []).slice(0, 6).forEach((m) => {
    suggestions.push({
      type: "mineral",
      id: m.application_number || m.licence_number || `mining-${suggestions.length}`,
      label: `${m.commodity || "Mineral"} · ${m.province || ""}`,
      detail: `${m.district || ""} · ${m.status || ""} · ${m.area_ha ?? "?"} ha`,
    });
  });
  (mkt.data?.land_opportunities || []).slice(0, 4).forEach((l) => {
    suggestions.push({
      type: "parcel",
      id: l.parcel_number || `parcel-${suggestions.length}`,
      label: `${l.parcel_number || "Parcel"} · ${l.province || ""}`,
      detail: `${l.district || ""} · ${l.land_type || ""} · ${l.area_ha ?? "?"} ha`,
    });
  });
  (risk.data?.risks || []).slice(0, 6).forEach((r) => {
    suggestions.push({
      type: "risk",
      id: r.id,
      label: r.risk,
      detail: `${r.category} · ${r.impact} impact · owner ${r.owner}`,
    });
  });
  (perf.data?.dashboards || []).slice(0, 5).forEach((p) => {
    suggestions.push({
      type: "province",
      id: `province-${p.province}`,
      label: p.province,
      detail: `Executive score ${p.executive_score} · #${p.national_rank}`,
    });
  });
  return { suggestions, loading: mkt.isLoading || risk.isLoading || perf.isLoading };
};

const StatusPill = ({ text, color }) => (
  <span className="rounded-sm px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em]"
        style={{ background: color + "25", color }}>
    {text}
  </span>
);

export const ExecutiveWatchlist = () => {
  const { watchlist, addWatch, removeWatch, isWatched } = usePresentation();
  const { suggestions, loading } = useSuggested();
  const [query, setQuery] = useState("");
  const filtered = suggestions.filter((s) => !query || s.label.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="p-6 space-y-4" data-testid="page-executive-watchlist">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: "#C99A2E60", background: "linear-gradient(120deg, rgba(7,27,43,0.95), rgba(11,53,88,0.85), rgba(201,154,46,0.2))" }}>
        <Star size={32} className="text-[#C99A2E] mt-1" />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px] text-[#C99A2E]">Executive Watchlist</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">Your pinned intelligence</h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-3xl">
            Pin any parcel, mineral right, province, risk, revenue line or KPI. Preferences are stored per user.
            Nothing here modifies operational records.
          </div>
        </div>
      </div>

      <div className="rounded-sm border border-white/10 p-3" style={{ background: "rgba(11,53,88,0.35)" }}>
        <div className="flex items-center gap-2">
          <Star size={14} className="text-[#C99A2E]" />
          <div className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#C99A2E]">
            My watchlist ({watchlist.length})
          </div>
        </div>
        {watchlist.length === 0 && (
          <div className="mt-3 text-[12px] text-[#7C93A5] italic">
            No items yet. Pin from the suggestions below or from any cadastre page (star icon).
          </div>
        )}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
          {watchlist.map((w) => {
            const meta = TYPE_META[w.type] || TYPE_META.kpi;
            const Icon = meta.icon;
            return (
              <div key={w.type + "-" + w.id} className="rounded-sm border p-2.5 flex items-start gap-2"
                   data-testid={"watch-item-" + w.type + "-" + w.id}
                   style={{ borderColor: meta.accent + "40", background: "rgba(7,27,43,0.5)" }}>
                <Icon size={14} style={{ color: meta.accent }} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] text-[#F6F8FA] font-medium truncate">{w.label}</div>
                  <div className="text-[10.5px] font-mono text-[#7C93A5] truncate">{w.detail}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <StatusPill text={meta.label} color={meta.accent} />
                    <Link to={meta.to} className="text-[10px] font-mono uppercase text-[#B3C2CC] inline-flex items-center gap-1">
                      Open <ChevronRight size={9} />
                    </Link>
                    <button onClick={() => removeWatch(w)}
                            data-testid={"unwatch-" + w.type + "-" + w.id}
                            className="ml-auto text-[10px] font-mono uppercase text-[#E67E7E] inline-flex items-center gap-1">
                      <Trash2 size={10} /> Unpin
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-sm border border-white/10 p-3" style={{ background: "rgba(11,53,88,0.25)" }}>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#4FA3D1]">Suggested to pin</div>
          <div className="ml-auto flex items-center gap-2">
            <Search size={12} className="text-[#7C93A5]" />
            <input value={query} onChange={(e) => setQuery(e.target.value)}
                   placeholder="Filter suggestions"
                   data-testid="watch-filter"
                   className="bg-transparent border border-white/10 rounded-sm px-2 py-1 text-[11px] text-[#F6F8FA]" />
          </div>
        </div>
        {loading && <div className="text-[11px] text-[#7C93A5] italic mt-3">Loading live suggestions…</div>}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {filtered.slice(0, 30).map((s) => {
            const meta = TYPE_META[s.type] || TYPE_META.kpi;
            const Icon = meta.icon;
            const already = isWatched(s);
            return (
              <div key={s.type + "-" + s.id} className="rounded-sm border p-2.5 flex items-start gap-2"
                   style={{ borderColor: meta.accent + "30", background: "rgba(7,27,43,0.4)" }}>
                <Icon size={13} style={{ color: meta.accent }} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11.5px] text-[#F6F8FA] font-medium truncate">{s.label}</div>
                  <div className="text-[10px] font-mono text-[#7C93A5] truncate">{s.detail}</div>
                </div>
                <button onClick={() => addWatch(s)} disabled={already}
                        data-testid={"pin-" + s.type + "-" + s.id}
                        className="rounded-sm px-1.5 py-0.5 text-[10px] font-mono uppercase inline-flex items-center gap-1"
                        style={{
                          background: already ? "transparent" : meta.accent + "30",
                          color: already ? "#7C93A5" : meta.accent,
                          border: already ? "1px solid rgba(255,255,255,0.1)" : "none",
                        }}>
                  {already ? "Pinned" : (<><Plus size={9} /> Pin</>)}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ExecutiveWatchlist;
